import React from 'react';
import { MovementPattern } from '../interfaces/types';
import { Activity, AlertCircle, ArrowRight, Check, Clock, Home } from 'lucide-react';
import { analyzeMovementPatterns } from '../utils/analysisUtils';

interface MovementPatternAnalysisProps {
  patterns: MovementPattern[];
}

const MovementPatternAnalysis: React.FC<MovementPatternAnalysisProps> = ({ patterns }) => {
  // Analyze movement patterns
  const { abnormalPatterns, abnormalityScore, commonPaths, mostFrequentLocations } = analyzeMovementPatterns(patterns);
  
  // Format timestamp
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Get status color based on pattern type
  const getPatternStatusColor = (patternType: 'ROUTINE' | 'ANOMALOUS' | 'NEW'): string => {
    switch (patternType) {
      case 'ROUTINE': return 'bg-green-100 text-green-800';
      case 'ANOMALOUS': return 'bg-red-100 text-red-800';
      case 'NEW': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get score color based on deviation score
  const getScoreColor = (score: number): string => {
    if (score < 20) return 'text-green-500';
    if (score < 50) return 'text-yellow-500';
    return 'text-red-500';
  };
  
  // Format duration
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center mb-4">
        <Activity className="text-blue-500 mr-2" />
        <h3 className="text-lg font-semibold">Movement Pattern Analysis</h3>
      </div>
      
      <div className="mb-5">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-gray-600">Pattern Deviation Score</span>
          <span className={`font-semibold ${getScoreColor(abnormalityScore)}`}>
            {abnormalityScore.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${
              abnormalityScore < 20 ? 'bg-green-500' : 
              abnormalityScore < 50 ? 'bg-yellow-500' : 
              'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, abnormalityScore)}%` }}
          ></div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {abnormalityScore < 20 ? 'Normal movement patterns' : 
           abnormalityScore < 50 ? 'Slight deviations in movement patterns' :
           'Significant movement pattern abnormalities detected'}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="border rounded-lg p-3">
          <h4 className="text-sm font-medium mb-2">Common Movement Paths</h4>
          <div className="space-y-2">
            {commonPaths.length > 0 ? (
              commonPaths.map((path, index) => (
                <div key={index} className="text-sm">
                  <div className="flex items-center">
                    <Home size={16} className="text-gray-500 mr-1" />
                    {path.map((location, i) => (
                      <React.Fragment key={i}>
                        <span>{location}</span>
                        {i < path.length - 1 && <ArrowRight size={14} className="mx-1 text-gray-400" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No common paths detected yet</p>
            )}
          </div>
        </div>
        
        <div className="border rounded-lg p-3">
          <h4 className="text-sm font-medium mb-2">Most Frequent Locations</h4>
          <div className="grid grid-cols-2 gap-2">
            {mostFrequentLocations.map((location, index) => (
              <div key={index} className="bg-gray-50 rounded p-2 text-center">
                <span className="text-sm">{location}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <h4 className="text-sm font-medium mb-2">Recent Movement Patterns</h4>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {patterns.slice(0, 5).map((pattern, index) => (
          <div key={index} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <div className="flex items-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPatternStatusColor(pattern.patternType)}`}>
                    {pattern.patternType}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {formatTime(pattern.timestamp)}
                  </span>
                </div>
                <div className="flex items-center mt-2 text-sm">
                  {pattern.locationPath.map((location, i) => (
                    <React.Fragment key={i}>
                      <span>{location}</span>
                      {i < pattern.locationPath.length - 1 && <ArrowRight size={14} className="mx-1 text-gray-400" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center text-xs text-gray-500">
                  <Clock size={14} className="mr-1" />
                  <span>{formatDuration(pattern.duration)}</span>
                </div>
                <div className="mt-1 text-xs">
                  <span className={`${pattern.deviationFromBaseline > 30 ? 'text-red-500' : 'text-gray-500'}`}>
                    {pattern.deviationFromBaseline.toFixed(1)}% deviation
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600">
              <div>
                <span className="block text-gray-500">Speed</span>
                <span>{pattern.averageSpeed.toFixed(1)} steps/sec</span>
              </div>
              <div>
                <span className="block text-gray-500">Pauses</span>
                <span>{pattern.pauseFrequency}</span>
              </div>
              <div>
                <span className="block text-gray-500">Confidence</span>
                <span className={pattern.confidence > 80 ? 'text-green-500' : 'text-yellow-500'}>
                  {pattern.confidence}%
                </span>
              </div>
            </div>
            
            {pattern.patternType === 'ANOMALOUS' && pattern.deviationFromBaseline > 40 && (
              <div className="mt-2 text-xs flex items-start text-red-600 bg-red-50 p-2 rounded">
                <AlertCircle size={14} className="mr-1 flex-shrink-0 mt-0.5" />
                <span>
                  Significant deviation detected. Increased pauses and unusual route selection may indicate spatial disorientation.
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MovementPatternAnalysis; 